/**
 * Transparent header over selected content elements.
 *
 * The page sets pages.hd_header_overlay (light|dark) and pages.hd_header_overlay_elements,
 * HeaderOverlay.html renders them as data-header-overlay / data-header-targets on #header.
 * This switches data-header-state between "overlay" (transparent) and "solid" (normal look);
 * the looks themselves live in the "Transparent header" section of app.css.
 *
 * Rules:
 *  - "overlay" while the selected elements together cover the whole bar of the header,
 *    otherwise "solid". Adjacent selected elements keep the header transparent across the seam.
 *  - An open mobile menu (Header1/Header3) forces "solid": its panel below the header is white.
 *  - The header height is measured into --hd-header-overlay-offset (on start, load and resize),
 *    the first selected element is pulled up by it (CSS in HeaderOverlay.html). Header2 is
 *    measured with its top bar expanded, so the element does not jump when the top bar
 *    collapses on scroll.
 *
 * Deliberately independent of Alpine: Alpine starts on window load, far too late for the
 * first paint of the header.
 */

// Switch on in the browser console: localStorage.setItem('hdDebug', '1')
const DEBUG = (() => {
    try {
        return window.localStorage.getItem('hdDebug') === '1';
    } catch (e) {
        return false;
    }
})();

function log(...args) {
    if (DEBUG) {
        console.log('[HeaderOverlay]', ...args);
    }
}

export function initHeaderOverlay(header = document.getElementById('header')) {
    if (!header || !header.dataset.headerOverlay || !header.dataset.headerTargets) {
        return null;
    }

    const root = document.documentElement;
    // Header2: the top bar stays solid, only the main bar turns transparent.
    const bar = header.querySelector('.header2-main') || header;
    const topBar = header.querySelector('.header2-topbar');
    const menuButtons = Array.from(header.querySelectorAll('[aria-controls="main-menu-wrap"]'));
    const targetIds = header.dataset.headerTargets.split(/\s+/).filter(Boolean);

    let targets = [];
    let frame = null;

    const findTargets = () => {
        targets = targetIds.map((id) => document.getElementById(id)).filter(Boolean);
        if (targets.length !== targetIds.length) {
            log('some targets are not on the page', targetIds, targets.map((t) => t.id));
        }
    };

    const measure = () => {
        // A collapsed top bar still reports its full content height as scrollHeight.
        const collapsedTopBar = topBar ? topBar.scrollHeight - topBar.offsetHeight : 0;
        root.style.setProperty('--hd-header-overlay-offset', `${header.offsetHeight + collapsedTopBar}px`);
    };

    const isMenuOpen = () => menuButtons.some((button) => button.getAttribute('aria-expanded') === 'true');

    /** Do the selected elements together cover the header bar from top to bottom? */
    const coversBar = () => {
        const barRect = bar.getBoundingClientRect();
        const top = Math.max(barRect.top, 0);
        const bottom = barRect.bottom;
        if (bottom <= 0) {
            return false; // header scrolled away (static header), nothing to decide
        }
        const intervals = targets
            .map((target) => target.getBoundingClientRect())
            .filter((rect) => rect.height > 0 && rect.bottom > top && rect.top < bottom)
            .map((rect) => [rect.top, rect.bottom])
            .sort((a, b) => a[0] - b[0]);

        let reached = top;
        for (const [start, end] of intervals) {
            if (start > reached + 1) {
                break; // a gap of uncovered content behind the header
            }
            reached = Math.max(reached, end);
        }
        return reached >= bottom - 1;
    };

    const update = () => {
        frame = null;
        const menuOpen = isMenuOpen();
        const covered = coversBar();
        const state = !menuOpen && covered ? 'overlay' : 'solid';
        log('update', { menuOpen, covered, state, targets: targets.map((t) => t.id) });
        if (header.dataset.headerState !== state) {
            header.dataset.headerState = state;
        }
    };

    const schedule = () => {
        if (frame === null) {
            frame = window.requestAnimationFrame(update);
        }
    };

    findTargets();
    measure();
    update();

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', () => {
        measure();
        schedule();
    }, { passive: true });
    // Images, fonts and lazy content move the elements after the first paint.
    window.addEventListener('load', () => {
        findTargets();
        measure();
        schedule();
    }, { once: true });

    // Re-decide on header size changes, but do not re-measure the offset here: toggling the
    // mobile menu shows both icons for a single frame, and the pulled-up element would jump
    // with that transient height. The offset follows real layout changes via resize and load.
    if ('ResizeObserver' in window) {
        new ResizeObserver(schedule).observe(header);
    }
    if ('MutationObserver' in window && menuButtons.length) {
        const observer = new MutationObserver(() => {
            schedule();
            // Once more after the menu transition, when the header has settled again.
            window.setTimeout(schedule, 400);
        });
        menuButtons.forEach((button) => observer.observe(button, { attributes: true, attributeFilter: ['aria-expanded'] }));
    }

    // Diagnostics from the browser console: document.getElementById('header').hdHeaderOverlay.inspect()
    const api = {
        update,
        measure,
        inspect: () => ({ menuOpen: isMenuOpen(), covered: coversBar(), state: header.dataset.headerState, targets: targets.map((t) => t.id) }),
    };
    header.hdHeaderOverlay = api;
    return api;
}

export function registerHeaderOverlay() {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initHeaderOverlay(), { once: true });
    } else {
        initHeaderOverlay();
    }
}
